import React from "react";
import Section from "../ui/Section/Section";
import { Grid, Stack } from "../ui/Grid/Grid";
import { CardBody, CenterRow } from "../ui/Card/Card";
import SectionSkeleton from "../ui/SectionSkeleton/SectionSkeleton";
import KpiCard from "../ui/Card/KpiCard";
import ChartCard from "../ui/Card/ChartCard";
import Donut from "../charts/Donut";
import LegendTable from "../ui/LegendTable/LegendTable";
import HBarList from "../ui/HBarList/HBarList";
import SplitBar from "../ui/SplitBar/SplitBar";
import DeltaValue from "../ui/DeltaValue/DeltaValue";
import EmptyState from "../ui/EmptyState/EmptyState";
import { KPI, SECTIONS } from "../../constants";
import { fmt1, fmtN } from "../../functions/format";
import { pctChange } from "../../functions/mapEffectif";
import { usePilotageRhStore } from "../../store/usePilotageRhStore";

const { id, title } = SECTIONS.effectif;

const SKELETON_ROWS = [
    { cols: 4, cards: [1, 1, 1, 1] },
    { cols: 3, cards: [5, 5, 3] },
];

const EffectifSection = ({ data, isLoading }) => {
    const resetFilters = usePilotageRhStore((s) => s.resetFilters);

    if (isLoading && !data) {
        return (
            <Section id={id} title={title}>
                <SectionSkeleton rows={SKELETON_ROWS} />
            </Section>
        );
    }

    if (!data || data.headcount === 0) {
        return (
            <Section id={id} title={title}>
                <EmptyState
                    title="Aucun collaborateur ne correspond à ce périmètre"
                    description="Modifiez les filtres pour afficher l'effectif."
                    actionLabel="Réinitialiser les filtres"
                    onAction={resetFilters}
                />
            </Section>
        );
    }

    const { headcount: hc } = data;

    return (
        <Section id={id} title={title}>
            <Grid cols={4} $mb>
                <KpiCard
                    label={KPI.headcount.label}
                    unit={KPI.headcount.unit}
                    value={fmtN(hc)}
                    section={id}
                    delta={pctChange(hc, data.headcountPrevious)}
                />
                <KpiCard
                    label={KPI.etp.label}
                    unit={KPI.etp.unit}
                    value={fmt1(data.etp)}
                    section={id}
                    delta={pctChange(data.etp, data.etpPrevious)}
                />
                <KpiCard
                    label={KPI.effectif_moyen.label}
                    unit={KPI.effectif_moyen.unit}
                    value={fmtN(data.effectifMoyen)}
                    section={id}
                    delta={null}
                />
                <KpiCard
                    label={KPI.evolution_effectif.label}
                    unit={KPI.evolution_effectif.unit}
                    value={`${data.evolution >= 0 ? "+" : ""}${fmt1(
                        data.evolution
                    )}`}
                    section={id}
                    delta={null}
                />
            </Grid>

            <Grid cols={3}>
                <ChartCard title="Répartition par contrat" fill>
                    <CardBody $gap="10px">
                        <CenterRow>
                            <Donut
                                segments={data.contratRows.map((r) => ({
                                    value: r.value,
                                    color: r.color,
                                    tip: `${r.name} : ${fmtN(r.value)} collab.`,
                                }))}
                                center={fmtN(hc)}
                                sub="ACTIFS"
                            />
                        </CenterRow>
                        <LegendTable rows={data.contratRows} total={hc} />
                    </CardBody>
                </ChartCard>

                <ChartCard title="Répartition par département">
                    <HBarList items={data.deptRows} />
                </ChartCard>

                <Stack>
                    <ChartCard title="Catégorie professionnelle">
                        <SplitBar
                            left={{
                                label: "Cadres",
                                value: (
                                    <DeltaValue section={id} delta={null}>
                                        {data.cadresPct}%
                                    </DeltaValue>
                                ),
                                pct: data.cadresPct,
                                tip: `Cadres : ${fmtN(
                                    data.cadresCount
                                )} collab.`,
                            }}
                            right={{
                                label: "Non-cadres",
                                value: `${100 - data.cadresPct}%`,
                                tip: `Non-cadres : ${fmtN(
                                    hc - data.cadresCount
                                )} collab.`,
                            }}
                        />
                    </ChartCard>
                    <ChartCard title="Taux d’activité">
                        <SplitBar
                            left={{
                                label: "Temps plein",
                                value: (
                                    <DeltaValue section={id} delta={null}>
                                        {data.tempsPleinPct}%
                                    </DeltaValue>
                                ),
                                pct: data.tempsPleinPct,
                                tip: `Temps plein : ${fmtN(
                                    data.tempsPleinCount
                                )} collab.`,
                            }}
                            right={{
                                label: "Temps partiel",
                                value: `${100 - data.tempsPleinPct}%`,
                                tip: `Temps partiel : ${fmtN(
                                    hc - data.tempsPleinCount
                                )} collab.`,
                            }}
                        />
                    </ChartCard>
                </Stack>
            </Grid>
        </Section>
    );
};

export default EffectifSection;
